import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ArrowUpRight,
  ArrowLeft,
  Check,
  ChevronRight,
  Loader2,
  X,
  Sparkles,
} from "lucide-react";
import type { ReactNode, ButtonHTMLAttributes } from "react";
import "./Workflow.css";
import { Button as ShadcnButton } from "./ui/primitives/button";
import { Badge } from "./ui/primitives/badge";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/primitives/dialog";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "./ui/primitives/tooltip";
export function Logo({ compact = false }: { compact?: boolean }) {
  return (
    <a href="#/" className="logo" aria-label="AClip 首页">
      <img src="/favicon.svg" alt="" />
      {!compact && (
        <>
          <span>AClip</span>
          <span className="logo-note">STUDIO</span>
        </>
      )}
    </a>
  );
}
export function Button({
  children,
  variant = "default",
  loading = false,
  disabledReason,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "default" | "ghost" | "dark";
  loading?: boolean;
  disabledReason?: string;
}) {
  const blocked = props.disabled || loading;
  const explanation = blocked
    ? disabledReason ||
      (loading ? "正在处理，请等待完成。" : props.title || "当前操作暂不可用。")
    : "";
  const button = (
    <ShadcnButton
      variant={
        variant === "primary"
          ? "default"
          : variant === "dark"
            ? "secondary"
            : variant === "default"
              ? "outline"
              : "ghost"
      }
      className={`button ${variant} ${className}`}
      {...props}
      disabled={props.disabled || loading}
      aria-busy={loading || undefined}
      title={explanation || props.title}
      aria-description={explanation || undefined}
    >
      {loading && <Loader2 size={16} className="spin" />}
      {children}
    </ShadcnButton>
  );
  return button;
}
export function IconButton({
  children,
  label,
  disabledReason,
  tooltip = true,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  tooltip?: boolean;
  disabledReason?: string;
}) {
  const button = (
    <ShadcnButton
      variant="ghost"
      size="icon-sm"
      className="icon-button"
      aria-label={label}
      {...props}
      title={props.disabled ? disabledReason || label : props.title}
      aria-description={props.disabled ? disabledReason : undefined}
    >
      {children}
    </ShadcnButton>
  );
  return props.disabled || !tooltip ? (
    button
  ) : (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent sideOffset={6}>{label}</TooltipContent>
    </Tooltip>
  );
}
type ModalProps = {
  title: string;
  subtitle?: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
  className?: string;
  presentation: "page" | "dialog";
};
export function Modal({ presentation, ...props }: ModalProps) {
  return presentation === "page" ? (
    <FullPage {...props} />
  ) : (
    <ModalDialog {...props} />
  );
}
function ModalDialog({
  title,
  subtitle,
  children,
  onClose,
  wide = false,
  className = "",
}: Omit<ModalProps, "presentation">) {
  // Capture the opener before child autoFocus inputs can take focus.
  const returnFocus = useRef<HTMLElement | null>(
    document.activeElement as HTMLElement,
  );
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        aria-modal="true"
        showCloseButton={false}
        className={`modal ac-dialog ${wide ? "wide" : ""} ${className}`}
        {...(!subtitle ? { "aria-describedby": undefined } : {})}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (returnFocus.current?.isConnected) returnFocus.current.focus();
        }}
      >
        <div className="modal-head">
          <div>
            <DialogTitle>{title}</DialogTitle>
            {subtitle && <DialogDescription>{subtitle}</DialogDescription>}
          </div>
          <IconButton label="关闭" tooltip={false} onClick={onClose}>
            <X size={20} />
          </IconButton>
        </div>
        {children}
      </DialogContent>
    </Dialog>
  );
}
function FullPage({
  title,
  subtitle,
  children,
  onClose,
  wide = false,
  className = "",
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
  className?: string;
}) {
  const headingId = useId();
  const descriptionId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const before = document.activeElement as HTMLElement;
    const old = document.body.style.overflow;
    const appRoot = document.getElementById("root");
    const wasInert = appRoot?.inert;
    if (appRoot) appRoot.inert = true;
    document.body.style.overflow = "hidden";
    ref.current?.focus();
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") close.current();
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.body.style.overflow = old;
      if (appRoot) appRoot.inert = !!wasInert;
      document.removeEventListener("keydown", handler);
      before?.focus();
    };
  }, []);
  return createPortal(
    <section
      className="utility-page"
      aria-label={title}
      ref={ref}
      tabIndex={-1}
    >
      <header className="utility-page-topbar">
        <span onClick={onClose}>
          <Logo />
        </span>
        <Button variant="ghost" onClick={onClose}>
          <ArrowLeft size={15} />
          返回上一页
        </Button>
      </header>
      <main className={`utility-page-main ${wide ? "wide" : ""}`}>
        <div className="utility-page-heading">
          <h1 id={headingId}>{title}</h1>
          {subtitle && <p id={descriptionId}>{subtitle}</p>}
        </div>
        <div
          className={`modal utility-page-content ${wide ? "wide" : ""} ${className}`}
        >
          {children}
        </div>
      </main>
    </section>,
    document.body,
  );
}
export function StatusPill({
  children,
  good = false,
}: {
  children: ReactNode;
  good?: boolean;
}) {
  return (
    <Badge variant="outline" className={`status-pill ${good ? "good" : ""}`}>
      {good ? <Check size={12} /> : <span className="status-dot" />}
      {children}
    </Badge>
  );
}
export function Empty({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <div className="empty">
      <span className="empty-icon">
        <Sparkles size={25} />
      </span>
      <h3>{title}</h3>
      <p>{description}</p>
      {children}
    </div>
  );
}
export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="section-label">
      {children}
      <ArrowUpRight size={14} />
    </div>
  );
}
export function LinkArrow() {
  return <ChevronRight size={16} />;
}
